import React, { useState, useEffect } from 'react';
import { getAllQuestionBanks, refreshAllData } from '../utils/generateQuestionBanks';
import './QuestionBankList.css';

function QuestionBankList({ selectedSubject, onSelectBank }) {
  const [questionBanks, setQuestionBanks] = useState([]);
  const [subjectName, setSubjectName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completionStatus, setCompletionStatus] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [allWrongAnswers, setAllWrongAnswers] = useState([]);

  const fetchQuestionBanks = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllQuestionBanks(forceRefresh);
      
      if (data.subjects && data.subjects[selectedSubject]) {
        const banks = data.subjects[selectedSubject].banks || [];
        const name = data.subjects[selectedSubject].name || selectedSubject;
        console.log(`获取到${banks.length}个题库`);
        setQuestionBanks(banks);
        setSubjectName(name);
        
        // 加载每个题库的完成状态
        loadCompletionStatus(banks);
        
        if (forceRefresh) {
          setLastRefreshTime(new Date());
        }
      } else {
        console.error('未找到选中学科的题库:', selectedSubject);
        setQuestionBanks([]);
        setError(`未找到学科"${selectedSubject}"的题库数据`);
      }
    } catch (error) {
      console.error('获取题库失败:', error);
      setQuestionBanks([]);
      setError('获取题库数据失败，请检查网络连接');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedSubject) {
      // 页面加载时总是强制刷新，确保获取最新数据
      const forceRefresh = true;
      fetchQuestionBanks(forceRefresh);
      localStorage.setItem(`subjectRefresh-${selectedSubject}`, new Date().toISOString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubject]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshAllData();
      await fetchQuestionBanks(true);
      localStorage.setItem(`subjectRefresh-${selectedSubject}`, new Date().toISOString());
      
      console.log('✅ 学科题库数据已刷新');
    } catch (error) {
      console.error('刷新失败:', error);
    } finally {
      setRefreshing(false);
    }
  };
  
  // 加载题库完成状态
  const loadCompletionStatus = (banks) => {
    const status = {};
    const allWrongAnswersList = [];
    
    banks.forEach(bank => {
      const records = localStorage.getItem(`questionRecords-${bank.id}`);
      const wrongAnswers = localStorage.getItem(`wrongAnswers-${bank.id}`);
      
      // 收集错题数据
      if (wrongAnswers) {
        try {
          const wrongAnswersList = JSON.parse(wrongAnswers);
          // 只收集未掌握的错题
          wrongAnswersList
            .filter(wrongAnswer => wrongAnswer.status !== 'mastered')
            .forEach(wrongAnswer => {
              allWrongAnswersList.push({
                ...wrongAnswer,
                bankId: bank.id,
                bankTitle: bank.title
              });
            });
        } catch (error) {
          console.error(`解析题库 ${bank.id} 的错题数据失败:`, error);
        }
      }
      
      if (records) {
        const recordsData = JSON.parse(records);
        const totalCount = bank.count || 0;
        const answeredCount = Object.keys(recordsData).length;
        const correctCount = Object.values(recordsData).filter(record => record.isCorrect).length;
        const wrongCount = answeredCount - correctCount;
        
        const completionRate = totalCount > 0 ? (answeredCount / totalCount) * 100 : 0;
        const errorRate = answeredCount > 0 ? (wrongCount / answeredCount) * 100 : 0;
        
        status[bank.id] = {
          totalCount,
          answeredCount,
          correctCount,
          wrongCount,
          completionRate,
          errorRate,
          completed: answeredCount >= totalCount
        };
      }
    });
    
    // 按错误次数降序排序
    allWrongAnswersList.sort((a, b) => (b.wrongCount || 1) - (a.wrongCount || 1));
    setAllWrongAnswers(allWrongAnswersList);
    setCompletionStatus(status);
  };
  
  // 获取题库完成状态的样式类
  const getBankStatusClass = (bankId) => {
    const status = completionStatus[bankId];
    if (!status) return '';
    
    if (status.completed) {
      if (status.wrongCount === 0) {
        return 'bank-status-perfect'; // 全部完成且全部正确
      } else if (status.errorRate < 10) {
        return 'bank-status-excellent'; // 全部完成且错误率低于10%
      } else if (status.errorRate < 30) {
        return 'bank-status-good'; // 全部完成且错误率低于30%
      } else {
        return 'bank-status-completed'; // 全部完成但错误率较高
      }
    } else if (status.completionRate > 0) {
      return 'bank-status-inprogress'; // 部分完成
    }
    
    return ''; // 未开始
  };
  
  // 获取进度条样式
  const getProgressBarStyle = (bankId) => {
    const status = completionStatus[bankId];
    if (!status) return { width: '0%' };
    
    return { width: `${status.completionRate}%` };
  };
  
  // 获取进度条颜色类
  const getProgressBarClass = (bankId) => {
    const status = completionStatus[bankId];
    if (!status) return '';
    
    if (status.errorRate === 0 && status.completionRate > 0) {
      return 'progress-perfect'; // 没有错题
    } else if (status.errorRate < 10) {
      return 'progress-excellent'; // 错误率低于10%
    } else if (status.errorRate < 30) {
      return 'progress-good'; // 错误率低于30%
    } else {
      return 'progress-need-review'; // 错误率较高
    }
  };
  
  // 获取状态文本用于徽章显示
  const getStatusText = (status) => {
    if (!status || status.completionRate === 0) return '';
    
    if (status.completed) {
      if (status.wrongCount === 0) {
        return '全对'; // 全部完成且全部正确
      } else {
        return `${Math.round(status.errorRate)}%错误`; // 完成但有错误
      }
    } else {
      return `${Math.round(status.completionRate)}%`;
    }
  };

  // 开始全科错题练习
  const startAllWrongQuestionsPractice = () => {
    // 过滤出未掌握的错题
    const unmasteredWrongAnswers = allWrongAnswers.filter(q => q.status !== 'mastered');
    
    if (unmasteredWrongAnswers.length === 0) {
      alert('当前学科暂无未掌握的错题记录');
      return;
    }
    
    // 创建一个虚拟的"全科错题"题库
    const virtualBank = {
      id: `all-wrong-${selectedSubject}`,
      title: `${subjectName} - 全科错题练习`,
      path: '', // 虚拟题库不需要路径
      count: unmasteredWrongAnswers.length,
      isVirtualAllWrongQuestionBank: true,
      subjectId: selectedSubject,
      allWrongQuestions: unmasteredWrongAnswers
    };
    
    // 设置标记表示要开始全科错题练习
    localStorage.setItem(`startAllWrongQuestionPractice-${selectedSubject}`, JSON.stringify(virtualBank));
    
    // 选择第一个有错题的题库作为载体
    if (allWrongAnswers.length > 0 && onSelectBank) {
      // 找到第一个错题对应的题库
      const firstWrongAnswer = allWrongAnswers[0];
      const targetBank = questionBanks.find(bank => bank.id === firstWrongAnswer.bankId);
      if (targetBank) {
        onSelectBank(targetBank);
      }
    }
  };

  const handleBankClick = (bank) => {
    if (onSelectBank) {
      onSelectBank(bank);
    }
  };

  if (loading) {
    return <div className="loading-container">加载题库中...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <p className="sub-text">请选择其他学科或刷新页面重试</p>
      </div>
    );
  }

  if (questionBanks.length === 0) {
    return (
      <div className="no-banks">
        <p>当前学科暂无题库</p>
        <p className="sub-text">请选择其他学科或等待题库更新</p>
      </div>
    );
  }

  return (
    <div className="question-bank-list">
      <div className="header-section">
        <h2>{subjectName} 题库列表 ({questionBanks.length}个)</h2>
        <div className="header-actions">
          {allWrongAnswers.filter(q => q.status !== 'mastered').length > 0 && (
            <button 
              className="all-wrong-questions-btn"
              onClick={startAllWrongQuestionsPractice}
              title={`练习该学科所有未掌握错题（共${allWrongAnswers.filter(q => q.status !== 'mastered').length}题，按错误次数排序）`}
            >
              📚 做全部错题 ({allWrongAnswers.filter(q => q.status !== 'mastered').length})
            </button>
          )}
          <button 
            className={`refresh-btn ${refreshing ? 'refreshing' : ''}`}
            onClick={handleRefresh}
            disabled={refreshing}
            title="刷新题库数据"
          >
            🔄 {refreshing ? '刷新中...' : '刷新'}
          </button>
          {lastRefreshTime && (
            <span className="last-refresh">
              上次刷新: {lastRefreshTime.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>
      <div className="banks-container">
        {questionBanks.map((bank) => {
          const status = completionStatus[bank.id];
          const statusText = status ? getStatusText(status) : '';
          
          return (
            <div
              key={bank.id}
              className={`bank-card ${getBankStatusClass(bank.id)}`}
              onClick={() => handleBankClick(bank)}
              data-count={statusText}
            >
              <h3>{bank.title}</h3>
              <div className="bank-info">
                <span>题目数量: {bank.count || 0}</span>
                {status && status.answeredCount > 0 && (
                  <div className="completion-badge">
                    <span>
                      进度: {status.answeredCount}/{status.totalCount}
                      {status.wrongCount > 0 && ` (错题: ${status.wrongCount})`}
                    </span>
                    <div className="progress-bar-container">
                      <div 
                        className={`progress-bar ${getProgressBarClass(bank.id)}`} 
                        style={getProgressBarStyle(bank.id)}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default QuestionBankList;